import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    CircularProgress,
    Divider,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { request } from "app/network";
import Swal from "sweetalert2";

interface ProductDetailDialogProps {
    open: boolean;
    onClose: () => void;
    goodsCode: string | null;
    darkMode?: boolean;
}

const ProductDetailDialog = ({ open, onClose, goodsCode, darkMode }: ProductDetailDialogProps) => {
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState<any>(null);

    useEffect(() => {
        if (open && goodsCode) {
            fetchDetail(goodsCode);
        } else {
            setProduct(null);
        }
    }, [open, goodsCode]);

    const fetchDetail = async (code: string) => {
        setLoading(true);
        try {
            // API: GetGoodsDetail
            // definition in giftishow.ts: url: "/api/v1/giftishow/goods/:goodsCode"
            const response = await request("Giftishow.GetGoodsDetail", {
                pathParams: { goodsCode: code },
            });

            if (response.data && response.data.code === "0000") {
                if (response.data.result && response.data.result.goodsDetail) {
                    setProduct(response.data.result.goodsDetail);
                } else {
                    // Fallback if structure is slightly different (e.g. just result)
                    setProduct(response.data.result);
                }
            } else {
                Swal.fire("오류", response.data.message || "상품 상세 정보를 불러오지 못했습니다.", "error");
                onClose();
            }
        } catch (error) {
            console.error("Failed to fetch product detail", error);
            Swal.fire("오류", "상품 상세 정보를 불러오는 중 오류가 발생했습니다.", "error");
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat("ko-KR").format(price);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    bgcolor: darkMode ? "#1a2035" : "#fff",
                    color: darkMode ? "#fff" : "inherit"
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #eee",
                    mb: 1
                }}
            >
                <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                    상품 상세 정보
                </MDTypography>
                <IconButton onClick={onClose} sx={{ color: darkMode ? "#fff" : "inherit" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ minHeight: "300px", p: 3 }}>
                {loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress color="info" />
                    </MDBox>
                ) : product ? (
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={5}>
                            <MDBox
                                component="img"
                                src={product.goodsImgB || product.goodsImgS} // Use Big image if available
                                alt={product.goodsName}
                                width="100%"
                                borderRadius="lg"
                                shadow="md"
                                sx={{
                                    bgcolor: "#fff",
                                    objectFit: "contain",
                                    maxHeight: "400px",
                                    border: darkMode ? "none" : "1px solid #eee"
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <MDBox display="flex" flexDirection="column" gap={1}>
                                <MDTypography variant="caption" color="text" fontWeight="bold">
                                    {product.brandName}
                                </MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    {product.goodsName}
                                </MDTypography>

                                <MDBox mt={2} mb={2}>
                                    <MDTypography variant="h5" color="error" fontWeight="bold">
                                        {formatPrice(product.realPrice)}원
                                        {product.salePrice && product.salePrice !== product.realPrice && (
                                            <MDTypography
                                                component="span"
                                                variant="body2"
                                                color="text"
                                                sx={{ textDecoration: "line-through", ml: 1 }}
                                            >
                                                {formatPrice(product.salePrice)}원
                                            </MDTypography>
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <MDTypography variant="button" color="text" fontWeight="bold">상품코드</MDTypography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <MDTypography variant="body2" color={darkMode ? "white" : "dark"}>{product.goodsCode}</MDTypography>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <MDTypography variant="button" color="text" fontWeight="bold">유효기간</MDTypography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <MDTypography variant="body2" color={darkMode ? "white" : "dark"}>
                                            {product.limitDay ? `${product.limitDay}일` : "정보 없음"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <MDTypography variant="button" color="text" fontWeight="bold">교환처</MDTypography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <MDTypography variant="body2" color={darkMode ? "white" : "dark"}>{product.affiliate || product.brandName}</MDTypography>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <MDTypography variant="button" color="text" fontWeight="bold">상태</MDTypography>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <MDTypography variant="caption" fontWeight="bold"
                                            sx={{
                                                bgcolor: product.goodsStateCd === "SALE" ? "#4CAF50" : "#F44336",
                                                color: "#fff",
                                                px: 1, py: 0.5, borderRadius: "4px"
                                            }}>
                                            {product.goodsStateCd}
                                        </MDTypography>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                <MDTypography variant="button" color="text" fontWeight="bold" mb={1}>
                                    상품 설명
                                </MDTypography>
                                <MDTypography variant="body2" color={darkMode ? "white" : "dark"} sx={{ whiteSpace: "pre-wrap" }}>
                                    {product.content || "설명 없음"}
                                </MDTypography>

                            </MDBox>
                        </Grid>
                    </Grid>
                ) : (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                        <MDTypography color="text">상품 정보를 찾을 수 없습니다.</MDTypography>
                    </MDBox>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #eee" }}>
                <MDButton onClick={onClose} variant="gradient" color="dark">
                    닫기
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default ProductDetailDialog;
